import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { PlayerItem } from "../components/player/interfaces";

/** Fallback ratio for interactive sources until the real video size is known. */
export const DEFAULT_INTERACTIVE_RATIO = 32 / 9;

const getStreamForItem = (item: PlayerItem): MediaStream | undefined => {
  const d = item.getData();
  if (d.type === "video_session") return d.videoSession.stream;
  if (d.type === "stream") return d.mediaStream;
  return undefined;
};

const getMediaId = (item: PlayerItem): string | undefined => item.getData().extra?.mediaSession?.id;

const isInteractive = (item: PlayerItem): boolean =>
  item.getData().extra?.mediaSession?.extra?.playerFit === "interactive";

/** Ratio from config-provided dimensions (source settings), if valid. */
const ratioFromDimensions = (extra: any): number | undefined =>
  extra?.width > 0 && extra?.height > 0 ? extra.width / extra.height : undefined;

/**
 * Shared skeleton of the two ratio hooks: `fallbacks` declares which ids are
 * interactive and their best ratio known without measurement (config dimensions
 * or {@link DEFAULT_INTERACTIVE_RATIO}); `measure` samples the live value.
 * Measurements are polled (dimensions only become available once the stream
 * flows / metadata is loaded, and can change on resolution adaptation) and win
 * over the fallback. Every id in `fallbacks` is guaranteed an entry.
 */
const useMeasuredRatios = (
  fallbacks: Record<string, number>,
  measure: (id: string) => number | undefined,
): Record<string, number> => {
  const [measured, setMeasured] = useState<Record<string, number>>({});
  const fallbacksRef = useRef(fallbacks);
  fallbacksRef.current = fallbacks;
  const measureRef = useRef(measure);
  measureRef.current = measure;

  useEffect(() => {
    const sample = () => {
      setMeasured((prev) => {
        let next = prev;
        let changed = false;
        Object.keys(fallbacksRef.current).forEach((id) => {
          const ratio = measureRef.current(id);
          if (!ratio) return;
          if (Math.abs((prev[id] ?? 0) - ratio) > 0.001) {
            if (!changed) {
              next = { ...prev };
              changed = true;
            }
            next[id] = ratio;
          }
        });
        return changed ? next : prev;
      });
    };
    sample();
    const interval = setInterval(sample, 1000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const result: Record<string, number> = {};
    Object.keys(fallbacks).forEach((id) => {
      result[id] = measured[id] ?? fallbacks[id];
    });
    return result;
  }, [fallbacks, measured]);
};

/**
 * Aspect ratios of the interactive live sources, keyed by mediaSession id.
 * Measured from the video track settings; falls back to the source's
 * config-declared dimensions (`mediaSession.extra.width/height`), then to
 * {@link DEFAULT_INTERACTIVE_RATIO}. Every interactive item has an entry.
 */
export const useInteractiveAspectRatios = (items: PlayerItem[] | undefined): Record<string, number> => {
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const fallbacks = useMemo(() => {
    const result: Record<string, number> = {};
    (items ?? []).forEach((item) => {
      if (!isInteractive(item)) return;
      const id = getMediaId(item);
      if (!id) return;
      result[id] = ratioFromDimensions(item.getData().extra?.mediaSession?.extra) ?? DEFAULT_INTERACTIVE_RATIO;
    });
    return result;
  }, [items]);

  const measure = useCallback((id: string) => {
    const item = (itemsRef.current ?? []).find((i) => getMediaId(i) === id);
    if (!item) return undefined;
    const settings = getStreamForItem(item)?.getVideoTracks?.()[0]?.getSettings?.();
    if (!settings?.width || !settings?.height) return undefined;
    return settings.width / settings.height;
  }, []);

  return useMeasuredRatios(fallbacks, measure);
};

/**
 * VOD variant of {@link useInteractiveAspectRatios}: filters the interactive
 * VODs itself (`extra.playerFit`, frozen at record time) and returns their
 * ratios keyed by vod id — so `id in result` doubles as the interactive flag.
 * There is no MediaStream to sample: the ratio is read from the <video>
 * element inside each tile's container, falling back to the dimensions frozen
 * on the VOD's `extra` (from the live source's config), then to
 * {@link DEFAULT_INTERACTIVE_RATIO}.
 */
export const useInteractiveVodAspectRatios = (
  vods: VodOutputDTO[],
  getContainer: (vodId: string) => HTMLElement | null,
): Record<string, number> => {
  const getContainerRef = useRef(getContainer);
  getContainerRef.current = getContainer;

  const fallbacks = useMemo(() => {
    const result: Record<string, number> = {};
    vods.forEach((v) => {
      // `extra.mediaExtra` = the live media's `extra`, frozen at record time
      // (the backend's `extra` is a wider bag the recording chain writes into).
      const mediaExtra = v.extra?.mediaExtra;
      if (mediaExtra?.playerFit !== "interactive") return;
      result[v.id] = ratioFromDimensions(mediaExtra) ?? DEFAULT_INTERACTIVE_RATIO;
    });
    return result;
  }, [vods]);

  const measure = useCallback((id: string) => {
    const video = getContainerRef.current(id)?.querySelector("video");
    if (!video?.videoWidth || !video?.videoHeight) return undefined;
    return video.videoWidth / video.videoHeight;
  }, []);

  return useMeasuredRatios(fallbacks, measure);
};
